'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

interface SearchBarProps {
    placeholder?: string;
    className?: string;
}

export default function SearchBar({ placeholder = 'Cari...', className = '' }: SearchBarProps) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const currentSearch = searchParams.get('search') || '';
    const [term, setTerm] = useState('');

    // Sync with URL params if they change externally (e.g. back button)
    useEffect(() => {
        setTerm(currentSearch);
    }, [currentSearch]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params = new URLSearchParams(searchParams.toString());

        // Always reset to page 1 when searching
        params.delete('page');

        if (term.trim()) {
            params.set('search', term.trim());
        } else {
            params.delete('search');
        }

        router.replace(`?${params.toString()}`, { scroll: false });
    };

    const clearSearch = () => {
        setTerm('');
        const params = new URLSearchParams(searchParams.toString());
        params.delete('search');
        params.delete('page');
        router.replace(`?${params.toString()}`, { scroll: false });
    };

    return (
        <form onSubmit={handleSearch} className={`relative max-w-xl mx-auto ${className}`}>
            <div className="relative">
                <input
                    type="text"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder={placeholder}
                    className="w-full pl-5 pr-12 py-3 rounded-full border border-gray-200 focus:border-unmulGreen focus:ring-2 focus:ring-unmulGreen/20 shadow-sm transition-all outline-none"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                    {term && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            className="p-2 text-gray-400 hover:text-red-500 transition"
                            title="Clear search"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                    <button
                        type="submit"
                        className="p-2 text-gray-400 hover:text-unmulGreen transition"
                        title="Search"
                    >
                        <FontAwesomeIcon icon={faSearch} />
                    </button>
                </div>
            </div>
        </form>
    );
}
